import { useEffect } from "react";
import { SITE } from "@/constants/site";
import { useLang } from "@/i18n/LangContext";
import { toEnglishPath } from "@/i18n/routes";

const BASE = "https://www.magaliclouzet.com";

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, hreflang, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"][hreflang="${hreflang}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

// `path` est TOUJOURS le chemin canonique français — les URLs EN et les
// balises hreflang sont dérivées automatiquement.
export const Seo = ({ title, description, path = "/", type = "website", schemas = [], ogImage = `${BASE}/assets/og-image.jpg` }) => {
  const { lang, t } = useLang();

  useEffect(() => {
    const frPath = path;
    const enPath = toEnglishPath(frPath);
    const selfPath = lang === "en" ? enPath : frPath;

    const fullTitle = title.includes("Magali Clouzet") ? title : `${title} | Magali Clouzet`;
    document.title = fullTitle;
    document.documentElement.lang = t.meta.htmlLang;
    setMeta("name", "description", description);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", `${BASE}${selfPath}`);
    setMeta("property", "og:site_name", SITE.name);
    setMeta("property", "og:locale", t.meta.ogLocale);
    setMeta("property", "og:locale:alternate", t.meta.ogLocaleAlternate);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${BASE}${selfPath}`);

    // Ciblage linguistique (hreflang)
    setLink("alternate", "fr", `${BASE}${frPath}`);
    setLink("alternate", "en", `${BASE}${enPath}`);
    setLink("alternate", "x-default", `${BASE}${frPath}`);

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "page-schema";
    script.text = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
    const old = document.getElementById("page-schema");
    if (old) old.remove();
    document.head.appendChild(script);
    return () => script.remove();
  }, [title, description, path, type, schemas, lang, t]);

  return null;
};

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: `${BASE}${item.path}`,
  })),
});

export const professionalServiceSchema = (lang = "fr") => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${BASE}/#organization`,
  name: lang === "en" ? "Magali Clouzet — Strategic marketing consultant" : "Magali Clouzet — Consultante marketing stratégique",
  description:
    lang === "en"
      ? "Strategic marketing consultant for SMEs, start-ups and micro-businesses: brand strategy, marketing diagnostic, Go-To-Market launch plans, outsourced marketing management (Fractional CMO)."
      : "Consultante marketing stratégique pour PME, start-ups et TPE : stratégie de marque, diagnostic marketing, plan de lancement Go-To-Market, direction marketing externalisée (Fractional CMO).",
  url: BASE,
  email: SITE.email,
  sameAs: [SITE.linkedin],
  areaServed: ["Nice", "Sophia Antipolis", "Cannes", "Antibes", "Monaco", "France"],
  priceRange: lang === "en" ? "Quote on request" : "Sur devis",
});
